const REDIRECT_CHECK_ORIGIN = 'https://redirect-check.invalid';

/**
 * Returns a same-origin path from the sign-in redirect query.
 *
 * The query is attacker-controlled. Assignment to `window.location.href`
 * navigates to an absolute URL or a protocol-relative URL. A valid value
 * starts with one `/`. The function rejects a leading `//`, `\`, a scheme,
 * and control characters. It returns `/` when the value fails the check.
 */
export function safeRedirectPath(value: unknown): string {
  if (typeof value !== 'string' || !isSameOriginRedirectPath(value)) {
    return '/';
  }

  return value;
}

function isSameOriginRedirectPath(value: string): boolean {
  // One leading slash.
  // `//` is protocol-relative and leaves this origin.
  // A scheme such as `https:` or `javascript:` does not start with `/`.
  if (!value.startsWith('/') || value.startsWith('//')) {
    return false;
  }

  // Browsers map `\` to `/` in an HTTP URL, so `/\host` becomes `//host`.
  if (value.includes('\\')) {
    return false;
  }

  // Browsers delete tab and newline before they parse a URL.
  // The check also rejects every other C0 control.
  if (/[\u0000-\u001F\u007F]/.test(value)) {
    return false;
  }

  // The checks above already reject a scheme, `//`, `\`, and C0 controls.
  // This parse is a second check for a parser that still reads another origin.
  try {
    return new URL(value, REDIRECT_CHECK_ORIGIN).origin === REDIRECT_CHECK_ORIGIN;
  } catch {
    return false;
  }
}
